// Final Analytical Review Card V2 — the final analytical review screen. The review is computed by the server from the statements
// ("Including audit adjustments"), the same engine without the adjusting entries ("Unadjusted figures from client data") and
// the prior year; the screen shows it with what form 316(a) holds (remarks, the evaluation, sign-offs). Explanations are
// optional and never block anything; large movements are tinted for information. AI drafts only on the user's click, through
// the firm's AI gate; a draft is marked "AI draft — confirm" until the user confirms or edits it. Locked engagement: read-only.
// Batch 33 · FAR-F5 (owner 20-Oct-2026: an explicit Save button, no auto-save): opening and Refresh compute and show only;
// remarks, AI drafts kept, confirmations, expectation overrides, the auditor's own expectations, the evaluation answers and the
// recomputed figures are written ONLY by the Save of this screen (one request); "Unsaved changes" while there are any; leaving
// with unsaved changes asks first (useUnsavedGuard); Cancel puts back what is saved.
// Step 12-F4 · G9 (owner: "taking much time to load in every attempt"): the review is computed in the background and STORED
// (backend far_store.py). The screen shows the stored review at once; while a refresh runs it says "Updating… (figures as of
// DD-MMM-YYYY HH:mm)" — the dashboard's wording (StoredUpdating) — and reloads every few seconds until the figures are current
// (the unsaved edits are kept). Never built yet: "Updating… (figures not calculated yet)". Refresh queues the background refresh.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import SignOffBar from "@/components/SignOffBar";
import { SCB_TEXT, useUnsavedGuard } from "@/components/SaveCancelBar";
import { fmtDateTimeFirm } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { fmt, fill } from "@/components/far/FarParts";
import { errText } from "@/lib/i18n/1250_b34g";   // Batch 34 · P51-S5
import { LinesSection, RatiosSection, PlanningSection, ExpectationsSection, RelationshipsSection, ConclusionSection }
  from "@/components/far/FarSections";
import FarMonthly from "@/components/far/FarMonthly";
import UpdatingNote, { isUpdating } from "@/components/dashboard/StoredUpdating";

export const FAR_POLL_MS = 4000;          // Step 12-F4 · G9: reload while the stored review is being refreshed
export const FAR_POLL_MAX = 150;          // ≈ 10 minutes, then the user can press Refresh

export const FAR_FORM_ID = "final_analytical_review";

const isNoAiKey = (e) => {
  const d = e?.response?.data || {};
  const code = d.error_code || d.detail?.code || d.detail?.error_code || d.code;
  return code === "AI_ALLOCATION_EXHAUSTED" || e?.response?.status === 402;
};

const NO_EDITS = { remarks: {}, confirm: [], work_done: {}, overrides: {}, add: [], remove: [], evaluation: {} };
export const hasEdits = (ed) => Object.keys(ed.remarks).length > 0 || ed.confirm.length > 0 || Object.keys(ed.work_done).length > 0
  || Object.keys(ed.overrides).length > 0 || ed.add.length > 0 || ed.remove.length > 0 || Object.keys(ed.evaluation).length > 0;

// the review as shown: the saved review with the unsaved edits on top
export function withEdits(data, ed) {
  if (!data) return data;
  const rem = (r) => {
    let x = Object.prototype.hasOwnProperty.call(ed.remarks, r.key) ? { ...(r.remark || {}), ...ed.remarks[r.key] } : r.remark;
    if (x && ed.confirm.includes(r.key)) x = { ...x, ai_draft: false };
    return x;
  };
  const lines = asArray(data.lines).map((l) => ({ ...l, remark: rem(l),
    work_done: Object.prototype.hasOwnProperty.call(ed.work_done, l.key) ? { ...(l.work_done || {}), text: ed.work_done[l.key] } : l.work_done }));
  const byKey = Object.fromEntries(lines.map((l) => [l.key, l]));
  const exps = asArray(data.expectations).filter((x) => !(x.custom && ed.remove.includes(x.id))).map((x) => {
    const ov = ed.overrides[x.key];
    let row = { ...x, remark: rem(x) };
    if (ov && ov.clear) row = { ...row, expected: x.computed_expected ?? x.expected, override: null,
      difference: x.actual !== null && x.actual !== undefined ? x.actual - (x.computed_expected ?? x.expected) : x.difference };
    else if (ov) row = { ...row, computed_expected: x.computed_expected ?? x.expected, expected: ov.expected,
      difference: x.actual !== null && x.actual !== undefined ? x.actual - ov.expected : null,
      override: { expected: ov.expected, reason: ov.reason, by: "", pending: true } };
    return row;
  });
  ed.add.forEach((a) => {
    const actual = byKey[a.line_key]?.cy_adj;
    exps.push({ key: `exp:pending:${a.tmp}`, tmp: a.tmp, pending: true, kind: "custom", label: a.label, formula_en: a.formula,
      expected: a.expected, actual: actual ?? null, difference: actual !== null && actual !== undefined ? actual - a.expected : null,
      available: actual !== null && actual !== undefined, custom: true, line_key: a.line_key, remark: null });
  });
  const c = data.conclusion || {};
  const items = asArray(c.items).map((it) => (ed.evaluation[it.id] ? { ...it, ...ed.evaluation[it.id] } : it));
  return { ...data, lines, ratios: asArray(data.ratios).map((r) => ({ ...r, remark: rem(r) })), expectations: exps,
           conclusion: { ...c, items } };
}

export default function FinalAnalyticalReview({ engId }) {
  const { t, lang } = useI18n();
  const auth = useAuth();
  const userKey = auth?.user?.id || "anon";        // Batch 33 · B33-3: the column widths are remembered per user
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState(NO_EDITS);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [aiMsg, setAiMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const polls = useRef(0);

  // t is read through a ref: a new t on a re-render must not reload the review (it would drop the unsaved edits)
  const tRef = useRef(t);
  tRef.current = t;
  const load = useCallback(async () => {
    if (!engId) return;
    try {
      const r = await api.get(`/far/${engId}`);
      setData(r.data); setError("");
    } catch (e) { setError(toErrMsg(e, tRef.current("far.loadFailed"))); }
  }, [engId]);

  // PR 51 · P51-B1: another engagement — drop the unsaved edits and the busy / AI state first, so nothing typed on A shows (or
  // is saved) on B; the page also keys this screen by engagement (FinalAnalyticalReviewPage)
  useEffect(() => {
    setEdits(NO_EDITS); setData(null); setError(""); setBusy(false); setBusyKey(""); setAiMsg(""); setSaving(false);
  }, [engId]);
  useEffect(() => { load(); }, [load]);
  // Step 12-F4 · G9: while the stored review is refreshed in the background, reload it every FAR_POLL_MS (edits are kept)
  const stored = data?.stored;
  const updating = isUpdating(stored) && !stored?.failed;
  useEffect(() => {
    if (!updating) { polls.current = 0; return undefined; }
    if (polls.current >= FAR_POLL_MAX) return undefined;
    const id = setTimeout(() => { polls.current += 1; load(); }, FAR_POLL_MS);
    return () => clearTimeout(id);
  }, [updating, data, load]);

  const locked = !!data?.locked;
  const dirty = !locked && hasEdits(edits);
  const L = SCB_TEXT[lang === "ar" ? "ar" : "en"];

  const save = async () => {
    setSaving(true);
    const body = {};
    if (Object.keys(edits.remarks).length) body.remarks = edits.remarks;
    if (edits.confirm.length) body.confirm = edits.confirm;
    if (Object.keys(edits.work_done).length) body.work_done = edits.work_done;
    if (Object.keys(edits.overrides).length) body.overrides = edits.overrides;
    if (edits.add.length) body.add_expectations = edits.add.map(({ tmp, ...a }) => a);
    if (edits.remove.length) body.remove_expectations = edits.remove;
    if (Object.keys(edits.evaluation).length) body.evaluation = edits.evaluation;
    try {
      const r = await api.post(`/far/${engId}/save`, body);
      setData(r.data); setEdits(NO_EDITS);
      toast.success(r.data?.nothing_to_save ? t("far.nothingToSave") : L.savedOk);
      return true;
    } catch (e) { toast.error(errText(e, lang, t("far.saveFailed"))); return false; }   // Batch 34 · P51-S5: EN + AR
    finally { setSaving(false); }
  };
  useUnsavedGuard(dirty, lang, save);

  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post(`/far/${engId}/refresh`); polls.current = 0; setData(r.data); toast.success(t("sbC.refreshing")); }
    catch (e) { toast.error(toErrMsg(e, t("far.loadFailed"))); }
    finally { setBusy(false); }
  };

  const setRemark = (key, text) => setEdits((ed) => ({ ...ed, remarks: { ...ed.remarks, [key]: { text, ai_draft: false } } }));
  const confirmRemark = (key) => setEdits((ed) => (Object.prototype.hasOwnProperty.call(ed.remarks, key)
    ? { ...ed, remarks: { ...ed.remarks, [key]: { ...ed.remarks[key], ai_draft: false } } }
    : { ...ed, confirm: ed.confirm.includes(key) ? ed.confirm : [...ed.confirm, key] }));
  const setWorkDone = (key, text) => setEdits((ed) => ({ ...ed, work_done: { ...ed.work_done, [key]: text } }));
  const setEvaluation = (id, ans) => setEdits((ed) => ({ ...ed, evaluation: { ...ed.evaluation, [id]: ans } }));

  const draft = async (keys) => {
    const body = keys ? { keys } : { highlighted: true };
    if (!keys && !asArray(data?.lines).some((l) => l.highlight)) { toast.info?.(t("far.noHighlighted")); return; }
    setBusyKey(keys ? keys[0] : "*"); setAiMsg("");
    try {
      const r = await api.post(`/far/${engId}/ai-draft`, body, { _silent: true });
      const drafts = r.data?.drafts || {};
      setEdits((ed) => ({ ...ed, remarks: { ...ed.remarks, ...drafts } }));
      toast.success(t("far.drafted"));
    } catch (e) {
      if (isNoAiKey(e)) setAiMsg(t("far.noAiKey"));
      else toast.error(toErrMsg(e, t("far.remarkFailed")));
    } finally { setBusyKey(""); }
  };

  const addExpectation = (row) => {
    setEdits((ed) => ({ ...ed, add: [...ed.add, { ...row, tmp: `${Date.now()}-${ed.add.length}` }] }));
    return true;
  };
  const overrideExpectation = ({ key, ...body }) => {
    setEdits((ed) => ({ ...ed, overrides: { ...ed.overrides, [key]: body.clear ? { clear: true } : body } }));
    return true;
  };
  const removeExpectation = (row) => setEdits((ed) => (row.pending
    ? { ...ed, add: ed.add.filter((a) => a.tmp !== row.tmp) }
    : { ...ed, remove: ed.remove.includes(row.id) ? ed.remove : [...ed.remove, row.id] }));

  const exportFile = async (kind) => {
    try {
      const resp = await api.get(`/far/${engId}/export.${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(data?.form?.ref || "316(a)").replace(/[()]/g, "")}_Final_analytical_review_${String(engId).slice(0, 8)}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("far.exportFailed")); }
  };

  const view = useMemo(() => withEdits(data, edits), [data, edits]);

  if (error && !data) return <div className="rounded border p-6 text-sm text-red-700" data-testid="far-error">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground" data-testid="far-loading">{t("far.loading")}</div>;

  const missing = stored?.state === "missing";      // Step 12-F4 · G9: never built yet — no figures to show
  const th = data.thresholds || {};
  const ps = data.prior_source || {};
  // Batch 33 · B33-2: the form the review is kept on — 316(a) on an audit engagement, R316(a) on a review engagement
  const form = data.form || { id: FAR_FORM_ID, ref: "316(a)" };
  const title = (lang === "ar" ? form.title_ar : form.title) || t("far.title");
  const actions = { setRemark, confirmRemark, draft, setWorkDone, addExpectation, removeExpectation, overrideExpectation };
  const canSave = !locked && !saving && !missing && (dirty || !!data.unsaved_figures);
  return (
    <div className="space-y-4" data-testid="far-tab" data-locked={locked ? "1" : "0"}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold" data-testid="far-title">{title}</h2>
          <p className="text-xs text-muted-foreground" data-testid="far-intro">{fill(t("far.intro"), { ref: form.ref || "316(a)" })}</p>
          {data.computed_at && (
            <p className="text-[11px] text-muted-foreground" data-testid="far-computed-at">{t("far.computedAt")}: {fmtDateTimeFirm(data.computed_at)}</p>
          )}
          {data.saved_at && (
            <p className="text-[11px] text-muted-foreground" data-testid="far-saved-at">{t("far.savedAt")}: {fmtDateTimeFirm(data.saved_at)}</p>
          )}
          {updating && <UpdatingNote stored={stored} t={t} testid="far-updating" />}
          {stored?.failed && (
            <p className="text-xs text-red-700 dark:text-red-300" data-testid="far-store-failed">{t(missing ? "farStore.failedNew" : "farStore.failed")}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={locked || busy} onClick={refresh} data-testid="far-refresh">{t("far.refresh")}</Button>
          <Button size="sm" variant="outline" disabled={locked || !!busyKey || missing}
                  onClick={() => draft(null)} data-testid="far-draft-highlighted">{t("far.draftHighlighted")}</Button>
          <Button size="sm" variant="outline" disabled={missing} onClick={() => exportFile("pdf")} data-testid="far-export-pdf">{t("far.exportPdf")}</Button>
          <Button size="sm" variant="outline" disabled={missing} onClick={() => exportFile("xlsx")} data-testid="far-export-xlsx">{t("far.exportXlsx")}</Button>
        </div>
      </div>

      {missing && (
        <div className="rounded border px-3 py-6 text-sm text-muted-foreground" data-testid="far-pending" aria-busy={updating ? "true" : "false"}>
          {t("farStore.pendingHint")}
        </div>
      )}
      {!missing && !locked && (
        <div className={`sticky top-0 z-10 flex flex-wrap items-center justify-end gap-2 rounded border px-3 py-2 text-xs ${dirty ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "bg-white dark:bg-slate-900"}`}
             data-testid="far-save-bar" data-dirty={dirty ? "1" : "0"} role="status">
          <span className={dirty ? "font-medium text-amber-900 dark:text-amber-100" : "text-muted-foreground"} data-testid="far-save-bar-state">
            {saving ? L.saving : dirty ? L.unsaved : L.saved}</span>
          <Button size="sm" variant="outline" disabled={!dirty || saving} onClick={() => setEdits(NO_EDITS)}
                  data-testid="far-save-bar-cancel">{L.cancel}</Button>
          <Button size="sm" disabled={!canSave} onClick={save} data-testid="far-save-bar-save">{L.save}</Button>
        </div>
      )}
      {!missing && (<>
      {locked && <div className="rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs dark:bg-slate-900" data-testid="far-locked">{t("far.locked")}</div>}
      {!locked && data.unsaved_figures && (
        <div className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-950/30 dark:text-sky-100"
             data-testid="far-unsaved-figures">{t(data.saved ? "far.figuresChangedUnsaved" : "far.figuresNotSaved")}</div>
      )}
      {data.changed_after_signoff && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
             data-testid="far-changed-after-signoff">{t("far.changedAfterSignoff")}</div>
      )}
      {aiMsg && <div className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-950/30 dark:text-sky-100" data-testid="far-no-ai-key">{aiMsg}</div>}

      <div className="space-y-1 rounded border px-3 py-2 text-xs">
        <div data-testid="far-prior-note">{(lang === "ar" ? ps.note_ar : ps.note_en) || t("far.priorNote")}</div>
        <div className="text-muted-foreground" data-testid="far-thresholds">
          {th.available === false
            ? ((lang === "ar" ? th.reason_ar : th.reason_en) || t("far.noThresholds"))
            : fill(t("far.thresholds"), { pm: fmt(th.pm), ct: fmt(th.ct), pct: th.pct ?? 20 })}
        </div>
      </div>

      <LinesSection data={view} t={t} lang={lang} locked={locked} actions={actions} busyKey={busyKey} colKey={userKey} />
      <PlanningSection data={view} t={t} lang={lang} />
      <RatiosSection data={view} t={t} lang={lang} locked={locked} actions={actions} busyKey={busyKey} />
      <ExpectationsSection data={view} t={t} lang={lang} locked={locked} actions={actions} busyKey={busyKey} />
      <RelationshipsSection data={view} t={t} lang={lang} />
      <FarMonthly engId={engId} t={t} lang={lang} locked={locked} />
      <ConclusionSection data={view} t={t} lang={lang} locked={locked} onChange={setEvaluation} />

      <SignOffBar engId={engId} itemType="form" itemKey={form.id} refreshKey={data.saved_at} saveFirst={dirty || (!locked && !!data.unsaved_figures)} />
      </>)}
    </div>
  );
}
